import {
  defaultColorList,
  defaultTheme,
  parseThemeObject,
  stringifyColorList,
  stringifyThemeObject,
  Theme,
  toHtmlColor,
} from './color'
import { setupGame, cleanupGame } from './game'
import { defaultLayout, phoneDefaultLayout, stringifyLayout } from './layout'
import { create } from './lib/create'
import { resolveSearch } from './lib/urlParameter'
import { isMobile } from './util/isMobile'

export type Device = 'detect' | 'phone' | 'desktop' | ''

export type GamePlay = 'station' | 'switch'

export interface TrackOfThoughtConfig {
  autoPlay: boolean
  autoPlayOnEntry: boolean
  autoPlayOnExit: boolean
  colorList: string
  departureClearance: number
  device: Device
  duration: number
  gamePlay: GamePlay
  generateRetryCount: number
  gridHeight: number
  gridWidth: number
  layout: string
  level: number
  seed: number
  stationSeed: number
  trainSeed: number
  showColorIndices: boolean
  stationCount: number
  theme: Theme
  trainCount: number
  timeLimit: number  // Game time limit in seconds
}

function randomSeed() {
  return Math.floor(Math.random() * 2 ** 32)
}

function levelInfo(level: number, device: Device) {
  level = Math.max(level, 3)
  let trainCount = 14 + 3 * level
  let stationCount = level
  let big = 8 + Math.max(0, Math.floor(+(level - 11) / 2))
  let small = 6
  if (device === 'detect') {
    device = isMobile() ? 'phone' : 'desktop'
  }
  if (device === 'phone') {
    return {
      device: 'phone',
      gridHeight: big,
      gridWidth: small,
      trainCount,
      stationCount,
    }
  }
  return {
    device: 'desktop',
    gridHeight: small,
    gridWidth: big,
    trainCount,
    stationCount,
  }
}

function getConfig(location: Location) {
  return resolveSearch<TrackOfThoughtConfig>(location, {
    autoPlay: [() => false],
    autoPlayOnEntry: [({ autoPlay }) => autoPlay()],
    autoPlayOnExit: [({ autoPlay }) => autoPlay()],
    colorList: [() => stringifyColorList(defaultColorList)],
    departureClearance: [() => 3],
    device: [() => 'detect'],
    duration: [() => 100],
    gamePlay: [() => 'station'],  // Manual mode - player controls switches
    generateRetryCount: [() => (process.env.NODE_ENV === 'production' ? 200_000 : 2_000)],
    gridHeight: [({ level, device }) => levelInfo(level(), device()).gridHeight],
    gridWidth: [({ level, device }) => levelInfo(level(), device()).gridWidth],
    layout: [
      ({ device }) =>
        stringifyLayout(
          levelInfo(-1, device()).device === 'phone' ? phoneDefaultLayout : defaultLayout,
        ),
    ],
    level: [() => 0],
    seed: [() => randomSeed()],
    stationSeed: [({ seed }) => seed()],
    trainSeed: [({ seed }) => seed()],
    showColorIndices: [() => false],
    stationCount: [({ level }) => levelInfo(level(), '').stationCount],
    theme: [() => stringifyThemeObject(defaultTheme), parseThemeObject],
    trainCount: [({ level }) => levelInfo(level(), '').trainCount],
    timeLimit: [() => 60],  // 60 seconds (1 minute) default
  })
}

// Store current level for restart
let currentLevel = 0

function showLevelSelection() {
  // Cleanup any existing game
  cleanupGame()
  
  // Clear any remaining game elements
  document.querySelectorAll('body > canvas').forEach(el => el.remove())
  document.querySelectorAll('body > span').forEach(el => el.remove())
  document.querySelectorAll('body > div:not(.levelSelectionDiv)').forEach(el => el.remove())
  document.querySelectorAll('.settings-btn, .settings-panel').forEach(el => el.remove())
  document.body.classList.remove('gamemode')
  
  // Remove existing level selection if any
  document.querySelectorAll('.levelSelectionDiv').forEach(el => el.remove())
  
  let levelSelectionDiv = create('div', { className: 'levelSelectionDiv' }, [
    create('h1', { textContent: 'Train of Thought' }),
    create('p', { 
      textContent: 'Route trains to matching colored houses!',
      style: { textAlign: 'center', marginBottom: '20px', color: '#94a3b8' }
    }),
    create('h2', { 
      textContent: 'Select a level',
      style: { fontWeight: '400', fontSize: '1.5rem', textAlign: 'center', marginBottom: '20px' }
    }),
  ])
  document.body.appendChild(levelSelectionDiv)

  Array.from({ length: 14 - 3 + 1 }, (_, k) => {
    let levelNumber = `${k + 3}`
    levelSelectionDiv.appendChild(
      create('button', {
        textContent: levelNumber,
        className: 'levelSelectionButton',
        onclick: () => {
          levelSelectionDiv.style.opacity = '0%'
          // Use once option to prevent multiple triggers
          levelSelectionDiv.addEventListener('transitionend', () => {
            levelSelectionDiv.remove()
            startGame(parseInt(levelNumber))
          }, { once: true })
        },
      }),
    )
  })
}

function startGame(level: number) {
  currentLevel = level
  
  // Update URL
  let search = new URLSearchParams(location.search)
  search.set('level', String(level))
  // Generate new seed for each game
  search.set('seed', String(Math.floor(Math.random() * 2 ** 32)))
  history.pushState({}, '', '?' + search)
  
  const config = getConfig(location)
  
  // Set up restart and menu callbacks
  const onRestart = () => {
    startGame(currentLevel)
  }
  
  const onMenu = () => {
    // Clear URL level param
    let search = new URLSearchParams(location.search)
    search.delete('level')
    search.delete('seed')
    history.pushState({}, '', search.toString() ? '?' + search : location.pathname)
    showLevelSelection()
  }
  
  setupGame(config, onRestart, onMenu)
}

function main() {
  const config = getConfig(location)
  console.info(`&seed=${config.seed}`)
  console.info('config', config)

  console.info('theme', config.theme)

  document.documentElement.style.backgroundColor = toHtmlColor(config.theme.background)

  if (config.level > 0) {
    currentLevel = config.level
    const onRestart = () => startGame(currentLevel)
    const onMenu = () => {
      let search = new URLSearchParams(location.search)
      search.delete('level')
      search.delete('seed')
      history.pushState({}, '', search.toString() ? '?' + search : location.pathname)
      showLevelSelection()
    }
    setupGame(config, onRestart, onMenu)
  } else {
    showLevelSelection()
  }
}

main()
